import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, switchMap, take, tap } from 'rxjs';
import { DatalayerService } from '@analytics/datalayer';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { JourneyAnalytics } from '@analytics/journey';
import { Actions, ofType } from '@ngrx/effects';
import {
  type JssStateRootPlaceholderLoaded,
  JssStateActionTypes,
} from '@core/jss-routing';

/**
 * Service responsible for tracking navigation events and forwarding them to analytics.
 * Uses Angular's Router events to detect navigation changes and sends page view data
 * to the data layer for analytics tracking.
 */
@Injectable({
  providedIn: 'root',
})
export class NavigationEventsService {
  /** Router instance for listening to navigation events */
  readonly #router = inject(Router);
  /** Service for pushing analytics events to the data layer */
  readonly #datalayer = inject(DatalayerService);
  /** Service for pushing events to the Journey Analytics layer */
  readonly #journeyAnalytics = inject(JourneyAnalytics);
  readonly #actions$ = inject(Actions);

  /**
   * Observable pipeline that processes navigation events:
   * 1. Filters for NavigationEnd events
   * 2. Extracts the final URL after any redirects
   * 3. Wait for the CMS to finish loading the new page
   * 4. Pushes page view events to the data layer & Rudderstack
   * 5. Automatically unsubscribes when the service is destroyed
   */
  readonly #navigationEventsForwarder = this.#router.events.pipe(
    filter((e) => e instanceof NavigationEnd),
    map((e: NavigationEnd) => {
      return e.urlAfterRedirects;
    }),
    switchMap((url) =>
      this.#actions$.pipe(
        ofType<JssStateRootPlaceholderLoaded>(
          JssStateActionTypes.ROOT_PLACEHOLDER_LOADED
        ),
        take(1),
        map(() => url)
      )
    ),
    tap((url) => this.#datalayer.pushPageView(url)),
    tap(() => this.#journeyAnalytics.page()),
    takeUntilDestroyed()
  );

  /**
   * Initiates tracking of navigation events by subscribing to the events pipeline.
   * Should be called once during application initialization.
   */
  trackNavigationEvents() {
    this.#navigationEventsForwarder.subscribe();
  }
}
